import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './context/AuthContext';
import { SupplierAuthProvider } from './context/SupplierAuthContext';
import { ProtectedRoute } from './components/ProtectedRoute'; 
import ApprovalLinkHandler from './components/ApprovalLinkHandler';
import RequisitionApprovalLinkHandler from './components/RequisitionApprovalLinkHandler';
import SupplierProtectedRoute from './components/SupplierProtectedRoute';
import DashboardLayout from './components/layout/DashboardLayout'; 
import SupplierLayout from './components/layout/SupplierLayout';
import Home from './views/pages/CustomerPortal/Home';
import CustomerUpdate from './views/pages/Customers/CustomerUpdate';
import SimpleLogin from './views/pages/SimpleLogin';
import AuthCallback from './views/pages/AuthCallback';
import SupplierQuotation from './views/pages/Suppliers/SupplierQuotation';
import SupplierLogin from './views/pages/Suppliers/SupplierLogin';
import NewSupplier from './views/pages/Suppliers/NewSupplier';
import SupplierPasswordSetup from './views/pages/Suppliers/SupplierPasswordSetup';
import SupplierDashboard from './views/pages/Suppliers/SupplierDashboard';
import SupplierInvitations from './views/pages/Suppliers/SupplierInvitations';
import SupplierQuotations from './views/pages/Suppliers/SupplierQuotations';
import SupplierOrders from './views/pages/Suppliers/SupplierOrders';
import SupplierOrderDetails from './views/pages/Suppliers/SupplierOrderDetails';
import SupplierDocuments from './views/pages/Suppliers/SupplierDocuments';
import SupplierHelpdesk from './views/pages/Suppliers/SupplierHelpdesk';
import SupplierNewTicket from './views/pages/Suppliers/SupplierNewTicket';
import SupplierTicketDetail from './views/pages/Suppliers/SupplierTicketDetail';
import SupplierSLAManagement from './views/pages/Suppliers/SupplierSLAManagement';
import TicketRating from './views/pages/Helpdesk/TicketRating';
import Dashboard from './views/pages/Dashboard'; 
import Chats from './views/pages/Chats';
import Assets from './views/pages/Assets';
import AssetDetails from './views/pages/Assets/AssetDetails';
import Tanker from './views/pages/Tanker';
import Users from './views/pages/Users';
import Employees from './views/pages/Employees';
import ExternalBidding from './views/pages/ExternalBidding';
import Procurement from './views/pages/Procurement';
import Reports from './views/pages/Reports';
import Help from './views/pages/Help';
import HelpTickets from './views/pages/Helpdesk/HelpTickets';
import HelpdeskRatings from './views/pages/Helpdesk/HelpdeskRatings';
import PublicHelpViewer from './views/pages/Help/PublicHelpViewer';
import Documentation from './views/pages/Documentation';
import Calendar from './views/pages/Calendar';
import Budgets from './views/pages/Budgets';
import Sites from './views/pages/Sites';
import Roles from './views/pages/Roles';
import Categories from './views/pages/Categories';
import Departments from './views/pages/Departments';
import Posts from './views/pages/Posts';
import Notices from './views/pages/Notices';
import Areas from './views/pages/Areas';
import Customers from './views/pages/Customers';
import RFQs from './views/pages/RFQs';
import Specifications from './views/pages/Specifications';
import Requisitions from './views/pages/Requisitions';
import Settings from './views/pages/Settings';
import Updates from './views/pages/Updates';
import Profile from './views/pages/Users/Profile';
import PasswordSetup from './views/pages/PasswordSetup';
import NotFound from './views/pages/NotFound';
import Suppliers from './views/pages/Procurement/Suppliers';
import GLCodes from './views/pages/Procurement/GLCodes';
import SLAManagement from './views/pages/Procurement/SLAManagement';
import PermissionTest from './utils/permissionTest';
import ActivityLogs from './components/logs/ActivityLogs';
import SystemLogs from './components/logs/SystemLogs';
import ReloadPermissions from './components/ReloadPermissions';
// MMS Components - Enhanced Suite
import MMSDashboard from './views/pages/MMS/EnhancedDashboard';
import FieldWorkersList from './views/pages/MMS/FieldWorkers/WorkersList';
import JobCardsList from './views/pages/MMS/JobCards/EnhancedJobCardsList';
import LocationsList from './views/pages/MMS/Locations/LocationsList';
import MMSCalendar from './views/pages/MMS/Calendar/MMSCalendar';
import MMSReports from './views/pages/MMS/Reports/MMSReports';
import MMSAssets from './views/pages/MMS/Assets/Assets';
import PausedJobs from './views/pages/MMS/PausedJobs';
import Options from './views/pages/MMS/Options';
// MMS Mobile Web Components
import MMSMobileLogin from './views/pages/MMS/Mobile/MMSMobileLogin';
import MMSMobileJobs from './views/pages/MMS/Mobile/MMSMobileJobs';
import MMSMobileJobDetails from './views/pages/MMS/Mobile/MMSMobileJobDetails';
import './utils/config';
import './App.css';

// Legacy URL redirect component
const RedirectToSupplier = () => {
  const location = window.location;
  const newPath = location.pathname.replace('/documents/', '/supplier/');
  const newUrl = `${location.protocol}//${location.host}${newPath}${location.search}${location.hash}`;
  
  React.useEffect(() => {
    window.location.replace(newUrl);
  }, [newUrl]);
  
  return <div>Redirecting...</div>;
};

// Drop legacy /portal prefix for dashboard routes
const PortalDashboardRedirect = () => {
  const location = useLocation();
  const trimmedPath = location.pathname.replace(/^\/portal/, '') || '/';
  const target = `${trimmedPath}${location.search}${location.hash}`;
  return <Navigate to={target} replace />;
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🚨 App Error Boundary caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
            <p className="text-gray-600 mb-4">An error occurred. Please refresh the page.</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Redirect component for legacy specification URLs
function SpecificationRedirect() {
  const { specId } = useParams();
  return <Navigate to={`/dashboard/specifications/${specId}`} replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ToastContainer 
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
          <Routes>
            {/* MMS Mobile Web Routes - Separate auth system, outside main AuthProvider interference */}
            <Route path="/mms-mobile" element={<Navigate to="/mms-mobile/login" replace />} />
            <Route path="/mms-mobile/login" element={<MMSMobileLogin />} />
            <Route path="/mms-mobile/jobs" element={<MMSMobileJobs />} />
            <Route path="/mms-mobile/job/:jobId" element={<MMSMobileJobDetails />} />

            {/* Public routes */}
            <Route path="/" element={<Home />}/>
            <Route path="/portal/*" element={<Home />}/>
            <Route path="/portal/dashboard/*" element={<PortalDashboardRedirect />} />
            <Route path="/customers/update/:token" element={<CustomerUpdate />} />
            <Route path="/login" element={<SimpleLogin />} />
            <Route path="/login/forgot_password" element={<SimpleLogin />} />
            <Route path="/auth-callback" element={<AuthCallback />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/setup-password" element={<PasswordSetup />} />
            
            {/* Public Asset Details - QR Code scanning */}
            <Route path="/assets/details/:id" element={<AssetDetails />} />
            
            {/* Public Help Pages */}
            <Route path="/help" element={<PublicHelpViewer />} />
            <Route path="/help/:path" element={<PublicHelpViewer />} />
            
            {/* Helpdesk Ticket Rating (Public - No Auth Required) */}
            <Route path="/helpdesk/rate/:token" element={<TicketRating />} />
            
            {/* External Bidding Portal (Public - Employee OTP Auth) */}
            <Route path="/bidding" element={<ExternalBidding />} />
            
            {/* Legacy URL redirects: documents/rfq/* -> supplier/rfq/* */}
            <Route path="/documents/rfq/*" element={<RedirectToSupplier />} />
            
            {/* Supplier Password Setup - Public route */}
            <Route path="/portal/supplier/profile/:token" element={<SupplierPasswordSetup />} />
            
            {/* Supplier routes - Wrapped with SupplierAuthProvider */}
            <Route path="/supplier/*" element={
              <SupplierAuthProvider>
                <Routes>
                  <Route path="login" element={<SupplierLogin />} />
                  <Route path="rfq/:encryptedRef" element={<SupplierQuotation />} />
                  <Route path="" element={<SupplierProtectedRoute><SupplierLayout /></SupplierProtectedRoute>}>
                    <Route path="dashboard" element={<SupplierDashboard />} />
                    <Route path="invitations" element={<SupplierInvitations />} />
                    <Route path="quotations" element={<SupplierQuotations />} />
                    <Route path="orders" element={<SupplierOrders />} />
                    <Route path="order/:orderId" element={<SupplierOrderDetails />} />
                    <Route path="helpdesk" element={<SupplierHelpdesk />} />
                    <Route path="helpdesk/new" element={<SupplierNewTicket />} />
                    <Route path="helpdesk/ticket/:id" element={<SupplierTicketDetail />} />
                    <Route path="documents" element={<SupplierDocuments />} />
                    <Route path="sla" element={<SupplierSLAManagement />} />
                    <Route path="" element={<Navigate to="dashboard" replace />} />
                  </Route>
                </Routes>
              </SupplierAuthProvider>
            } />
            
            {/* Separate supplier registration route */}
            <Route path="/suppliers/new" element={
              <SupplierAuthProvider>
                <NewSupplier />
              </SupplierAuthProvider>
            } />
            
            {/* Redirect legacy specifications URLs to dashboard */}
            <Route path="/specifications/:specId" element={<SpecificationRedirect />} />
            <Route path="/specifications/*" element={<Navigate to="/dashboard/specifications" replace />} />
            
            {/* Redirect legacy chats URL to dashboard */}
            <Route path="/chats" element={<Navigate to="/dashboard/chats" replace />} />
            <Route path="/chats/*" element={<Navigate to="/dashboard/chats" replace />} />
            
            {/* Approval routes - authenticates user and redirects to appropriate page */}
            <Route path="/approve/:ticketId" element={<ApprovalLinkHandler />} />
            <Route path="/approve-requisition/:requisitionId" element={<RequisitionApprovalLinkHandler />} />
      
            {/* Protected admin routes */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="chats/*" element={<Chats />} />
              <Route path="assets/*" element={<Assets />} />
              <Route path="tanker/*" element={<Tanker />} />
              <Route path="mms/dashboard" element={<MMSDashboard />} />
              <Route path="mms/workers" element={<FieldWorkersList />} />
              <Route path="mms/jobcards" element={<JobCardsList />} />
              <Route path="mms/locations" element={<LocationsList />} />
              <Route path="mms/calendar" element={<MMSCalendar />} />
              <Route path="mms/reports" element={<MMSReports />} />
              <Route path="mms/assets" element={<MMSAssets />} />
              <Route path="mms/paused-jobs" element={<PausedJobs />} />
              <Route path="mms/options" element={<Options />} />
              <Route path="users/*" element={<Users />} />
              <Route path="employees/*" element={<Employees />} />
              <Route path="procurement/*" element={<Procurement />} />
              <Route path="reports/*" element={<Reports />} />
              <Route path="help_pages/*" element={<Help />} />
              <Route path="helpdesk" element={<HelpTickets />} />
              <Route path="helpdesk/ratings" element={<HelpdeskRatings />} />
              <Route path="documentation/*" element={<Documentation />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="budgets/*" element={<Budgets />} />
              <Route path="sites/*" element={<Sites />} />
              <Route path="roles/*" element={<Roles />} />
              <Route path="categories/*" element={<Categories />} />
              <Route path="departments/*" element={<Departments />} />
              <Route path="posts/*" element={<Posts />} />
              <Route path="notices/*" element={<Notices />} />
              <Route path="areas/*" element={<Areas />} />
              <Route path="customers/*" element={<Customers />} />
              <Route path="rfqs/*" element={<RFQs />} />
              <Route path="specifications/*" element={<Specifications />} />
              <Route path="requisitions/*" element={<Requisitions />} />
              <Route path="settings" element={<Settings />} />
              <Route path="updates" element={<Updates />} />
              <Route path="profile" element={<Profile />} />
              <Route path="settings/gl-codes" element={<GLCodes />} />
              <Route path="procurement/suppliers" element={<Suppliers />} />
              <Route path="procurement/sla" element={<SLAManagement />} />
              <Route path="logs/activity" element={<ActivityLogs />} />
              <Route path="logs/system" element={<SystemLogs />} />
              <Route path="permissions-test" element={<PermissionTest />} />
              <Route path="reload" element={<ReloadPermissions />} />
            </Route>
            
            {/* 404 route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;